import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { Typography } from '@mui/material';
import EmptyInfiniteListComponent from '../Components/ContactListWrapper/EmptyInfiniteListComponent';
import useAbortEffect from './useAbortEffect';

const Loading = function Loading() {
  const intl = useIntl();
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '5vh',
        padding: '24px',
      }}
    >
      <Typography variant="body1">{intl.messages.LOADING_DOT_DOT}</Typography>
    </div>
  );
};

export default function useInfiniteQuery(fetchDataCallback) {
  const [items, setItems] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [recordCount, setRecordCount] = useState(0);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const loadingRef = useRef();
  const observer = useRef();

  const setLoadingVisible = () => {
    if (loadingRef && loadingRef.current && loadingRef.current.style) {
      loadingRef.current.style.display = 'block';
    }
  };

  useEffect(() => {
    //removes old items on component unmount
    return () => {
      setItems([]);
    };
  }, []);

  const reload = useCallback(() => {
    // This manually starts the reset sequence
    setItems([]);
    setPageNumber(1);
    setLoading(true); // This will trigger the fetch effect
  }, []); // Empty dependencies mean this function is stable
  useEffect(() => {
    setError(null);
    setItems([]);
    setLoadingVisible();
    setPageNumber(1);
    setLoading(true);
  }, [fetchDataCallback]);

  useEffect(() => {
    setLoadingVisible();
    setLoading(true);
  }, [pageNumber]);

  const setNewItems = (newItems, newCount) => {
    const copyItems = [...items];
    copyItems.unshift(newItems);
    setItems(copyItems);
    setRecordCount(newCount);
  };

  const removeClickedItem = (itemId, newCount) => {
    if (recordCount > newCount) {
      const copyItems = [...items];
      const index = copyItems?.findIndex((x) => x.id === itemId);
      if (index >= 0) {
        copyItems?.splice(index, 1);
        setRecordCount(newCount);
        setItems(copyItems);
      }
    }
  };

  const updateItem = (updatedElement) => {
    const itemsCopy = [...items];
    const index = itemsCopy?.findIndex((x) => x.id === updatedElement.id);
    if (index >= 0) {
      itemsCopy?.splice(index, 1, updatedElement);
      setItems(itemsCopy);
    }
  };

  const updatePropertyValue = (itemId, propertyName, newValue) => {
    const copyItems = [...items];
    const index = items?.findIndex((x) => x.id === itemId);
    if (index >= 0) {
      copyItems[index] = { ...copyItems[index], [propertyName]: newValue };
      setItems(copyItems);
    }
  };

  useAbortEffect(
    (signal) => {
      if (!loading) return;
      fetchDataCallback(pageNumber, signal)
        ?.then((res) => {
          if (Array.isArray(res) && res.length === 0) return;

          const pages = res.headers['x-paging-pagecount']
            ? parseInt(res.headers['x-paging-pagecount'], 10)
            : null;
          const count = res.headers['x-paging-totalrecordcount']
            ? parseInt(res.headers['x-paging-totalrecordcount'], 10)
            : null;
          setItems([...items, ...res.data]);
          setError(null);
          setLoading(false);
          setRecordCount(count);
          setHasMore((pages || pageNumber) > pageNumber);
        })
        .catch((e) => {
          setLoading(false);
          setError(e);
        });
    },
    [loading, fetchDataCallback]
  );

  const lastItemElementRef = useCallback(
    (node) => {
      if (!loading) {
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting && hasMore) {
            setPageNumber(pageNumber + 1);
          }
        });
        if (node) observer.current.observe(node);
      } else if (loadingRef && loadingRef.current && loadingRef.current.style) {
        loadingRef.current.style.display = 'block';
      }
    },
    [loading, hasMore, recordCount]
  );

  //memoized so unrelated parent re-renders don't remount the whole list (it breaks transitions and local item state)
  const ObserverWrapper = useCallback(
    function ObserverWrapper({
      Component, //component to map items.length times
      EmptyListComponent = EmptyInfiniteListComponent, //Component shown when items.length === 0
    }) {
      if (items.length === 0 && !loading) {
        return EmptyListComponent ? (
          <EmptyListComponent />
        ) : (
          <EmptyInfiniteListComponent />
        );
      }
      return items.map((item, index) => {
        if (items.length === index + 1)
          return (
            <div ref={lastItemElementRef} key={`item-${item?.id}`}>
              {Component(item, index)}
              <div ref={loadingRef} style={{ display: 'none' }}>
                <Loading />
              </div>
            </div>
          );
        return Component(item, index);
      });
    },
    [items, loading, lastItemElementRef]
  );
  /***Props for ObserverWrapper component**/
  /*****/

  return {
    recordCount,
    items,
    loading,
    setNewItems,
    removeClickedItem,
    updatePropertyValue,
    updateItem,
    error,
    ObserverWrapper,
    reload,
  };
}
